import { Navigate, RouterProvider, createBrowserRouter } from "react-router"

import { ErrorPage } from "@/app/error"
import NotFound from "@/app/not-found"

const router = createBrowserRouter([
  {
    path: "/",
    lazy: () => import("./signup/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "/:username",
    lazy: () => import("./[username]/layout"),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        lazy: () => import("./[username]/page"),
      },
      {
        path: ":projectName",
        lazy: () => import("./[username]/[project-name]/layout"),
        children: [
          {
            path: "",
            lazy: () => import("./[username]/[project-name]/page"),
          },
          {
            path: "onboarding",
            lazy: () => import("./[username]/[project-name]/onboarding/page"),
          },
          {
            path: "settings",
            lazy: () => import("./[username]/[project-name]/settings/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/settings/page"),
              },
              {
                path: "collaborators",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/settings/collaborators/page"
                  ),
              },
              {
                path: "general",
                lazy: () =>
                  import("./[username]/[project-name]/settings/general/page"),
              },
              {
                path: "billing",
                lazy: () =>
                  import("./[username]/[project-name]/settings/billing/page"),
              },
              {
                path: "api-keys",
                lazy: () =>
                  import("./[username]/[project-name]/settings/api-keys/page"),
              },
              {
                path: "activity",
                lazy: () =>
                  import("./[username]/[project-name]/settings/activity/page"),
              },
            ],
          },
          {
            path: "metrics",
            lazy: () => import("./[username]/[project-name]/metrics/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/metrics/page"),
              },
              {
                path: "details",
                lazy: () =>
                  import("./[username]/[project-name]/metrics/details/page"),
              },
              {
                path: "settings",
                lazy: () =>
                  import("./[username]/[project-name]/metrics/settings/page"),
              },
            ],
          },
          {
            path: "relays",
            lazy: () => import("./[username]/[project-name]/relays/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/relays/page"),
              },
              {
                path: "setup",
                lazy: () =>
                  import("./[username]/[project-name]/relays/setup/page"),
              },
              {
                path: "settings",
                lazy: () =>
                  import("./[username]/[project-name]/relays/settings/page"),
              },
              {
                path: "deploy",
                lazy: () =>
                  import("./[username]/[project-name]/relays/deploy/page"),
              },
            ],
          },
          {
            path: "dns",
            lazy: () => import("./[username]/[project-name]/dns/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/dns/page"),
              },
              {
                path: "setup",
                lazy: () =>
                  import("./[username]/[project-name]/dns/setup/page"),
              },
              {
                path: "settings",
                lazy: () =>
                  import("./[username]/[project-name]/dns/settings/page"),
              },
            ],
          },
          {
            path: "endpoints",
            lazy: () => import("./[username]/[project-name]/endpoints/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/endpoints/page"),
              },
              {
                path: "list",
                lazy: () =>
                  import("./[username]/[project-name]/endpoints/list/page"),
              },
              {
                path: "net-diagnostics",
                lazy: () =>
                  import("./[username]/[project-name]/net-diagnostics/page"),
              },
              {
                path: "net-diagnostics/report/:reportId",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/net-diagnostics/report/page"
                  ),
              },
              {
                path: ":nodeId",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/endpoints/[node-id]/page"
                  ),
              },
            ],
          },
          {
            path: "workflows",
            lazy: () => import("./[username]/[project-name]/workflows/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/workflows/page"),
              },
              {
                path: ":workflowId",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/workflows/[workflow-id]/page"
                  ),
              },
            ],
          },
          {
            path: "change-plan",
            lazy: () =>
              import("./[username]/[project-name]/change-plan/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/change-plan/page"),
              },
            ],
          },
        ],
      },
      {
        path: "profile",
        lazy: () => import("./[username]/settings/layout"),
        children: [
          {
            path: "",
            lazy: () => import("./[username]/settings/page"),
          },
          {
            path: "connected-accounts",
            lazy: () => import("./[username]/settings/connected-accounts/page"),
          },
          {
            path: "security",
            lazy: () => import("./[username]/settings/security/page"),
          },
          {
            path: "api-keys",
            lazy: () =>
              import("./[username]/[project-name]/settings/api-keys/page"),
          },
        ],
      },

      {
        path: "projects/new",
        lazy: () => import("./[username]/projects/new/page"),
      },
    ],
  },
  {
    path: "/staff",
    lazy: () => import("./staff/layout"),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        element: <Navigate to="/staff/users" replace />,
      },
      {
        path: "users",
        lazy: () => import("./staff/users/page"),
      },
      {
        path: "users/:userId",
        lazy: () => import("./staff/users/[userId]/page"),
      },
      {
        path: "projects",
        lazy: () => import("./staff/projects/page"),
      },
      {
        path: "projects/:projectId",
        lazy: () => import("./staff/projects/[projectId]/layout"),
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />,
          },
          {
            path: "overview",
            lazy: () => import("./staff/projects/[projectId]/tabs/overview"),
          },
          {
            path: "entitlements",
            lazy: () =>
              import("./staff/projects/[projectId]/entitlements/page"),
          },
          {
            path: "usage",
            lazy: () => import("./staff/projects/[projectId]/usage/page"),
          },
          {
            path: "metrics",
            lazy: () => import("./staff/projects/[projectId]/metrics/page"),
          },
          {
            path: "deploys",
            lazy: () => import("./staff/projects/[projectId]/deploys/page"),
          },
          {
            path: "collaborators",
            lazy: () =>
              import("./staff/projects/[projectId]/collaborators/page"),
          },
        ],
      },
      {
        path: "support-tickets",
        lazy: () => import("./staff/support-tickets/page"),
      },
      {
        path: "support-tickets/new",
        lazy: () => import("./staff/support-tickets/new/page"),
      },
      {
        path: "support-tickets/:ticketNumber",
        lazy: () => import("./staff/support-tickets/[ticketNumber]/page"),
      },
    ],
  },
  {
    path: "/login",
    errorElement: <ErrorPage />,
    children: [
      { path: "", lazy: () => import("./login/page") },
      { path: "forgot", lazy: () => import("./login/forgot/page") },
      { path: "reset/:token", lazy: () => import("./login/reset/page") },
    ],
  },
  {
    path: "invite/:code",
    lazy: () => import("./invite/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "/verify/:token",
    lazy: () => import("./verify/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "/signup",
    lazy: () => import("./signup/page"),
    errorElement: <ErrorPage />,
  },

  // static site bits
  {
    path: "/pricing",
    lazy: () => import("./pricing/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "legal",
    lazy: () => import("./legal/layout"),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        lazy: () => import("./legal/page"),
      },
      {
        path: "privacy",
        lazy: () => import("./legal/privacy/page"),
      },
      {
        path: "terms",
        lazy: () => import("./legal/terms/page"),
      },
    ],
  },
  {
    path: "/style",
    lazy: () => import("./style/page"),
    errorElement: <ErrorPage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
])

export function Router() {
  return <RouterProvider router={router} />
}
